import { getAdminApiUrl } from '@adsk/acs-sc-web-platform-api';
import { PRODUCT_IDS } from '@adsk/alloy-provider';
import {
  getConstructionApiUrl,
  SERVICES,
  SERVICE_ACCESSES,
  SERVICE_STATUSES,
  SERVICES_PRODUCTS_MAP,
} from '@adsk/acs-admin-ui-components-helpers';
import { USER_ACCESSES } from './constants';

export const buildCreateProjectCompanyRequest = (projectId, companyData) => [
  `${getAdminApiUrl()}/v1/projects/${projectId}/companies`,
  {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(companyData),
  },
];

export const buildCreateProjectUsersRequest = (
  projectId,
  usersData,
  useConstructionApi = false
) => {
  const url = useConstructionApi ? getConstructionApiUrl() : getAdminApiUrl();

  return [
    `${url}/v1/projects/${projectId}/users:import`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(usersData),
    },
  ];
};

export const buildGetProjectRequest = (projectId, useConstructionApi) => {
  const url = useConstructionApi ? getConstructionApiUrl() : getAdminApiUrl();

  return `${url}/v1/projects/${projectId}`;
};

export const sortRoles = (roles, locale) =>
  roles.sort((a, b) => a.name.localeCompare(b.name, locale));

export const filterActiveRoles = (response) =>
  response.filter((role) => role.status === 'active');

const isServiceUsedInACC = (serviceName) =>
  Object.values(SERVICES).includes(serviceName);

export const getProjectUserServicesAccess = (services, accessLevel) => {
  if (services === null) return [];

  const defaultAccess =
    accessLevel === USER_ACCESSES.MEMBER
      ? SERVICE_ACCESSES.MEMBER
      : SERVICE_ACCESSES.ADMIN;
  return services.reduce((filtered, service) => {
    const { status, serviceName } = service;
    if (
      status === SERVICE_STATUSES.ACTIVE ||
      status === SERVICE_STATUSES.ACTIVATING
    ) {
      filtered.push({
        serviceName: service.serviceName,
        access:
          !isServiceUsedInACC(serviceName) ||
          (serviceName === SERVICES.PROJECT_ADMIN &&
            accessLevel === USER_ACCESSES.MEMBER)
            ? SERVICE_ACCESSES.NONE
            : defaultAccess,
      });
    }

    return filtered;
  }, []);
};

const getProductFromServiceName = (serviceName) =>
  SERVICES_PRODUCTS_MAP.filter((e) => e.service === serviceName).map(
    (e) => e.product
  );

const getServiceAccess = (
  serviceName,
  serviceAccess,
  product,
  selectedProducts
) =>
  (serviceName !== SERVICES.PROJECT_ADMIN &&
    (product.length === 0 ||
      (product.length > 0 &&
        !product.some((p) => selectedProducts.includes(p))))) ||
  (serviceName === SERVICES.PROJECT_ADMIN &&
    serviceAccess === SERVICE_ACCESSES.MEMBER)
    ? SERVICE_ACCESSES.NONE
    : serviceAccess;

export const getMembersAccessFromSelectedProducts = (
  services,
  accessLevel,
  selectedProducts
) => {
  if (services === null) return [];

  const serviceAccess =
    accessLevel === USER_ACCESSES.MEMBER
      ? SERVICE_ACCESSES.MEMBER
      : SERVICE_ACCESSES.ADMIN;

  return services.reduce((filtered, service) => {
    const { status, serviceName } = service;

    if (
      status === SERVICE_STATUSES.ACTIVE ||
      status === SERVICE_STATUSES.ACTIVATING
    ) {
      const product = getProductFromServiceName(serviceName);
      filtered.push({
        serviceName,
        access: getServiceAccess(
          serviceName,
          serviceAccess,
          product,
          selectedProducts
        ),
      });
    }

    return filtered;
  }, []);
};

export const getInitialProductAccess = (currentProductId, products) => {
  const initialProductsAccess = [PRODUCT_IDS.DOCS, PRODUCT_IDS.INSIGHT];
  if (
    !initialProductsAccess.includes(currentProductId) &&
    products.some((p) => p.type === currentProductId && p.userAssignable)
  ) {
    initialProductsAccess.push(currentProductId);
  }

  return initialProductsAccess;
};

export const mockFetchWithToken = (url) =>
  new Promise((resolve, reject) => {
    let result;
    if (url === buildGetProjectRequest('1')) {
      result = {
        services: Object.values(SERVICES).map((s) => ({
          serviceName: s,
          status: SERVICE_STATUSES.ACTIVE,
        })),
      };
    }

    if (result) resolve(result);
    else reject();
  });

export const getUsersAccess = (
  useWriteConstructionApis,
  selectedAccessLevel,
  productsAccesses,
  showProducts,
  projectDataServices
) => {
  if (useWriteConstructionApis) {
    const accessLevel =
      selectedAccessLevel === USER_ACCESSES.MEMBER
        ? SERVICE_ACCESSES.MEMBER
        : SERVICE_ACCESSES.ADMIN;
    const access = productsAccesses.map((p) => ({
      key: p,
      access: accessLevel,
    }));
    if (accessLevel === SERVICE_ACCESSES.ADMIN)
      access.push({
        key: PRODUCT_IDS.PROJECT_ADMINISTRATION,
        access: SERVICE_ACCESSES.ADMIN,
      });

    return access;
  }

  return showProducts
    ? getMembersAccessFromSelectedProducts(
        projectDataServices,
        selectedAccessLevel,
        productsAccesses
      )
    : getProjectUserServicesAccess(projectDataServices, selectedAccessLevel);
};
